import { useProductDetailDialog } from "@/components/product/product-detail-dialog/ProductDetailDialogContext";
import { badgeVariants } from "@/components/ui/badge";
import type {
  CardsEvent,
  CatalogSearched,
  MasterProductCard,
  ProductCard,
  SupplierProductCard
} from "@/lib/order-assistant/contract";
import { formatMoney } from "@/lib/order-assistant/format";
import { cn } from "@repo/design-system/utils";
import { Package } from "lucide-react";
import { useState } from "react";

import { useOrderAssistant } from "./OrderAssistantProvider";

/** How a searched catalog is named. Keyed by the contract's `CatalogSearched`,
 *  so a new catalog fails the typecheck until it has text here. */
export const CATALOG_NAMES: Record<CatalogSearched, string> = {
  master: "SalonStocker catalog",
  supplier: "supplier catalogs",
  order_history: "your order history",
  none: "No catalog searched"
};

/** The caption before the list of searched catalogs. */
export const CATALOG_LABEL = "Searched";

/** The badge on a supplier product that has no SalonStocker listing. */
export const NOT_ON_SALONSTOCKER = "Not on SalonStocker yet";

/**
 * The product thumbnail, or a neutral placeholder when there is none or it
 * fails to load. Supplier images are hosted by the supplier, so they are
 * requested without a referrer.
 */
export function CardImage({
  src,
  supplierHosted,
  className
}: {
  src: string | null;
  supplierHosted: boolean;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const showsImage = !!src && !failed;
  return (
    <div
      className={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-md",
        // A picture is shown whole (`object-contain`), so a wide box has room
        // beside it. Most product photos have a white backdrop: with no box
        // colour that room is the card's own surface (white, and its hover tint
        // with it), where a grey box is a band down each side. The grey is only
        // for the placeholder.
        !showsImage && "bg-ui-bg-subtle",
        className
      )}
    >
      {showsImage ? (
        // Fills the box whatever the picture's proportions, centred and never
        // cropped: absolutely placed so a flex or percentage height cannot
        // collapse it to the picture's own (often tiny) size.
        <img
          src={src}
          alt=""
          loading="lazy"
          referrerPolicy={supplierHosted ? "no-referrer" : undefined}
          onError={() => setFailed(true)}
          // `mix-blend-multiply`: a photo's white backdrop takes the card's
          // colour, so it does not show as a white block on the hover tint.
          className="absolute inset-0 h-full w-full object-contain object-center mix-blend-multiply"
        />
      ) : (
        <Package className="text-ui-fg-muted h-6 w-6" aria-hidden="true" />
      )}
    </div>
  );
}

/** The shared badge look as a `span`: a card is a `button`, which may only
 *  hold phrasing content, and `Badge` renders a `div`. */
function Badge({
  variant,
  children,
  className
}: {
  variant: NonNullable<Parameters<typeof badgeVariants>[0]>["variant"];
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span className={cn(badgeVariants({ variant }), className)}>
      {children}
    </span>
  );
}

type Layout = CardsEvent["layout"];

/** The width of every tile, master or supplier. 13rem leaves the next tile
 *  peeking in on a phone and fits the "not on SalonStocker" badge on one line. */
const CARD_WIDTH = "w-52";

/** The price and badges of a tile sit last, pinned to the bottom of the card
 *  (`mt-auto`), after the reason line. Tiles in a row are stretched to one
 *  height, so the price lines up across them whether or not a card has a
 *  reason. A list card is as tall as its content, so it keeps the reason below
 *  the price. */
const TILE_BOTTOM = "order-last";

const cardShell =
  "border-ui-border-base bg-ui-bg-base hover:bg-ui-bg-base-hover focus-visible:shadow-borders-focus flex min-w-0 gap-3 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none";

function MasterCard({
  card,
  layout
}: {
  card: MasterProductCard;
  layout: Layout;
}) {
  const { openDialog } = useProductDetailDialog();
  const tile = layout !== "list";

  const open = () => {
    openDialog({
      offerId: card.offer_id,
      sellerName: card.seller_name,
      productName: card.title,
      thumbnail: card.thumbnail,
      // Prices a claimed supplier's offer and keeps the product-viewed event a
      // single-offer view. The multi-variant fields are set only when the
      // service knows the product type (contract section 5).
      pricingProductId: card.master_product_id,
      ...(card.product_type
        ? {
            productId: card.master_product_id,
            sellerId: card.seller_id,
            productType: card.product_type
          }
        : {})
    });
  };

  return (
    <button
      type="button"
      onClick={open}
      aria-label={`Open ${card.title}`}
      className={cn(cardShell, tile ? "w-full flex-col" : "w-full flex-row")}
    >
      <CardImage
        src={card.thumbnail}
        supplierHosted={false}
        className={tile ? "h-24 w-full" : "h-16 w-16"}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-y-1">
        <span className="text-ui-fg-base line-clamp-2 text-sm font-medium">
          {card.title}
        </span>
        <span className="text-ui-fg-subtle truncate text-xs">
          {[card.brand, card.seller_name].filter(Boolean).join(" · ")}
        </span>
        <span
          className={cn(
            "mt-auto flex flex-wrap items-center gap-x-2 gap-y-1 pt-1",
            tile && TILE_BOTTOM
          )}
        >
          <span className="text-ui-fg-base text-sm font-semibold">
            {formatMoney(card.price, card.currency)}
          </span>
          <Badge variant={card.in_stock ? "success" : "neutral"}>
            {card.in_stock ? "In stock" : "Out of stock"}
          </Badge>
        </span>
        {card.reason ? (
          <span className="text-ui-fg-subtle line-clamp-2 text-xs">
            {card.reason}
          </span>
        ) : null}
      </span>
    </button>
  );
}

function SupplierCard({
  card,
  layout
}: {
  card: SupplierProductCard;
  layout: Layout;
}) {
  const { openSupplierDetail } = useOrderAssistant();
  const tile = layout !== "list";
  const hasTrade = typeof card.trade_price === "number";

  return (
    <button
      type="button"
      onClick={() =>
        openSupplierDetail({
          supplierProductId: card.supplier_product_id,
          supplierVariantId: card.supplier_variant_id,
          notOnSalonStocker: card.master_product_id === null,
          // The detail route sends no currency: the card's is what it shows.
          currency: card.currency
        })
      }
      // The label replaces the content, so the supplier (what tells two
      // listings of one product apart) has to be in it.
      aria-label={`Open ${card.title}, ${card.seller_name}`}
      className={cn(cardShell, tile ? "w-full flex-col" : "w-full flex-row")}
    >
      <CardImage
        src={card.thumbnail}
        supplierHosted
        className={tile ? "h-24 w-full" : "h-16 w-16"}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-y-1">
        <span className="text-ui-fg-base line-clamp-2 text-sm font-medium">
          {card.title}
        </span>
        {/* The supplier is what tells two listings of one product apart (the
            same title and often the same photo), so it gets a line of its own
            that wraps rather than being cut off after the variant and brand. */}
        <span className="text-ui-fg-base line-clamp-2 text-xs">
          {card.seller_name}
        </span>
        {card.variant_title || card.brand ? (
          <span className="text-ui-fg-subtle truncate text-xs">
            {[card.variant_title, card.brand].filter(Boolean).join(" · ")}
          </span>
        ) : null}
        <span
          className={cn(
            "mt-auto flex flex-col gap-y-1 pt-1",
            tile && TILE_BOTTOM
          )}
        >
          {hasTrade ? (
            <span className="flex flex-col">
              <span className="text-ui-fg-base text-sm font-semibold">
                {formatMoney(card.trade_price as number, card.currency)}
                <span className="text-ui-fg-subtle ml-1 text-xs font-normal">
                  Your trade price
                </span>
              </span>
              {card.public_price !== null ? (
                <span className="text-ui-fg-subtle text-xs">
                  Public price {formatMoney(card.public_price, card.currency)}
                </span>
              ) : null}
            </span>
          ) : (
            <span className="text-ui-fg-base text-sm font-semibold">
              {card.public_price !== null ? (
                formatMoney(card.public_price, card.currency)
              ) : (
                <span className="text-ui-fg-subtle text-xs font-normal">
                  No price shown
                </span>
              )}
            </span>
          )}
          <span className="flex flex-wrap gap-1">
            {card.master_product_id === null ? (
              // The tile is wide enough for this on one line (CARD_WIDTH).
              <Badge variant="warning" className="whitespace-nowrap">
                {NOT_ON_SALONSTOCKER}
              </Badge>
            ) : null}
            {!card.available ? (
              <Badge variant="neutral">Unavailable</Badge>
            ) : null}
          </span>
        </span>
        {card.reason ? (
          <span className="text-ui-fg-subtle line-clamp-2 text-xs">
            {card.reason}
          </span>
        ) : null}
      </span>
    </button>
  );
}

function CardItem({ card, layout }: { card: ProductCard; layout: Layout }) {
  return card.kind === "master" ? (
    <MasterCard card={card} layout={layout} />
  ) : card.kind === "supplier" ? (
    <SupplierCard card={card} layout={layout} />
  ) : null;
}

/** The cards of an event this drawer can draw: unknown kinds are skipped. */
function drawableCards(event: CardsEvent): ProductCard[] {
  return event.cards.filter(
    (card) => card.kind === "master" || card.kind === "supplier"
  );
}

/**
 * The catalog an event's caption names, or `null` when it draws no cards (and so
 * has no caption). The answer's own "Searched:" line leaves out the catalogs
 * these captions name, and a catalog is captioned once per reply (see
 * `cardCaptions`), so its words show once however many cards events there are.
 */
function cardsCatalog(
  event: CardsEvent
): CardsEvent["catalog_searched"] | null {
  if (drawableCards(event).length === 0) return null;
  return event.catalog_searched === "supplier" ? "supplier" : "master";
}

/** The caption of one catalog in a reply: its name and the text to show. */
export interface CardsCaption {
  catalog: CardsEvent["catalog_searched"];
  text: string;
}

/**
 * The captions of one reply, keyed by the event that carries each: the first
 * event of every catalog. "One present_cards call per catalog" is only prompt
 * text, so a second call for the same catalog is possible; it must not repeat
 * the caption, and its suppliers join the one caption so none is lost.
 */
export function cardCaptions(
  events: readonly CardsEvent[]
): Map<CardsEvent, CardsCaption> {
  const first = new Map<CardsCaption["catalog"], CardsEvent>();
  // Only supplier events list suppliers; by seller id, so one is named once.
  const supplierNames = new Map<string, string>();
  for (const event of events) {
    const searched = cardsCatalog(event);
    if (searched === null) continue;
    if (!first.has(searched)) first.set(searched, event);
    if (searched === "supplier") {
      for (const supplier of event.suppliers ?? []) {
        supplierNames.set(supplier.seller_id, supplier.name);
      }
    }
  }
  const captions = new Map<CardsEvent, CardsCaption>();
  for (const [catalog, event] of first) {
    const names = catalog === "supplier" ? [...supplierNames.values()] : [];
    captions.set(event, {
      catalog,
      text:
        `${CATALOG_LABEL}: ${CATALOG_NAMES[catalog]}` +
        (names.length > 0 ? ` (${names.join(", ")})` : "")
    });
  }
  return captions;
}

/**
 * One `cards` event: the reply's caption for its catalog when this event carries
 * it (`caption` is null on a later event of the same catalog), then the cards in
 * the layout the model chose. Unknown card kinds are skipped, so a newer service
 * does not break an older drawer.
 */
export function CardsView({
  event,
  caption
}: {
  event: CardsEvent;
  caption: CardsCaption | null;
}) {
  const cards = drawableCards(event);
  if (cardsCatalog(event) === null) return null;

  const items = cards.map((card) => (
    <CardItem
      key={
        card.kind === "master"
          ? `${card.offer_id}`
          : `${card.supplier_product_id}-${card.supplier_variant_id}`
      }
      card={card}
      layout={event.layout}
    />
  ));

  return (
    <section aria-label={event.title ?? "Products found"} className="space-y-2">
      <header className="flex flex-col">
        <h3 className="text-ui-fg-base text-sm font-semibold">
          {event.title ?? "Products found"}
        </h3>
        {caption ? (
          <p className="text-ui-fg-subtle text-xs">{caption.text}</p>
        ) : null}
      </header>
      {event.layout !== "list" ? (
        // Carousel and grid are one layout here: a row of equal-width tiles that
        // scrolls sideways, with the next tile peeking in. A two-column grid
        // goes cramped in a drawer this narrow, and master and supplier rows
        // must line up with each other.
        <ul className="-mx-4 flex snap-x scroll-px-4 gap-3 overflow-x-auto px-4 pb-2">
          {items.map((item, index) => (
            <li
              key={index}
              className={cn("flex shrink-0 snap-start", CARD_WIDTH)}
            >
              {item}
            </li>
          ))}
        </ul>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((item, index) => (
            <li key={index} className="flex">
              {item}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
